import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import TrendingUp from '@mui/icons-material/TrendingUp';

import ELEMENT from 'common/element';
import { queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';

import Button from 'components/common/Button/Button';
import { GraphConfigType, GraphPlotConfigType } from 'components/dashboards/types';
import { createEventsPageUrl, DashboardPlotData } from 'components/dashboards/dashboardData';
import GraphPlotItem from 'components/dashboards/DashboardPage/GraphPlotItem';
import AddEditPlotsModal from 'components/dashboards/DashboardPage/modals/AddEditPlotsModal';

import './GraphPlotList.scss';

// const
const DEFAULT_PLOT_MODEL: GraphPlotConfigType = {
  color: '',
  facet: 'rate',
  label: 'count',
  filter: '',
};

export type Callback = () => void;
type AddGraphModalProps = {
  config: GraphConfigType;
  plotsToDisplay: GraphPlotConfigType[];
  unSavedPlots?: GraphPlotConfigType[];
  fullPage: boolean;
  addPlotCallback: (plotConfig: GraphPlotConfigType) => void;
  editPlotCallback: (plotIndex: number, plotConfig: GraphPlotConfigType) => void;
  deletePlotCallback?: (plotIndex: number) => void;
  showHideCallback?: (plotIndex: number, show: boolean) => void;
  plotData?: DashboardPlotData[];
};

/**
 *
 * Graph plot list used for both addGraphModal and DashboardViewGraph
 *
 * @prop  config             graph config object
 * @prop  plotsToDisplay     The plots to display, read-only
 * @prop  fullPage           if true, we are in a full page mode
 * @prop  plotData           plotData that is used to generate links
 * @prop  showHideCallback   callback to control show/hide plot
 * @prop  addPlotCallback    callback to add plot
 * @prop  editPlotCallback   callback to edit plot
 * @prop  deletePlotCallback callback to delete plot
 * @prop  unSavedPlots       The plots that serve as a list of temporary plots.
 *                           This prop is used exclusively with AddGraphModal since the plots in the
 *                           AddGraphModal can be unsaved.
 */

function GraphPlotList({
  config,
  plotsToDisplay,
  fullPage,
  plotData,
  showHideCallback,
  addPlotCallback,
  editPlotCallback,
  deletePlotCallback,
  unSavedPlots,
}: AddGraphModalProps): JSX.Element {

  const isBreakdown = config ? (!!config.breakdownFacet || !!config.query) : false;
  const [defaultPlot, setDefaultPlot] = useState(DEFAULT_PLOT_MODEL);
  const [selectedPlotIndex, setSelectedPlotIndex] = useState(-1);
  const [selectedPlot, setSelectedPlot] = useState({});
  const [showAddPlot, setShowAddPlot] = useState(false);
  const [showEditPlot, setShowEditPlot] = useState(false);
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;

  const editSelectedPlot = (incomingPlot: GraphPlotConfigType): void => {
    editPlotCallback(selectedPlotIndex, incomingPlot);
  };

  const openEditPlotModal = (index: number): void => {
    setSelectedPlotIndex(index);
    if (unSavedPlots) {
      // this component is used with AddGraphModal so we shouldn't look at this config.plots
      setSelectedPlot(unSavedPlots[index]);
    } else if (config.plots && config.plots[index]) {
      // this is for DashboardViewGraph
      setSelectedPlot(config.plots[index]);
    }
    setShowEditPlot(true);
  };

  const showHidePlot = (index: number, showPlot: boolean): void => {
    if (showHideCallback) {
      showHideCallback(index, showPlot);
    }
  };

  const openAddPlotModal = (): void => {
    setSelectedPlotIndex(plotsToDisplay.length);
    setDefaultPlot(DEFAULT_PLOT_MODEL);
    setShowAddPlot(true);
  };

  const plotList = plotsToDisplay.map((plot, index) => {
    const key = `AddGraphModal__PlotEntry--${index}`;
    return (
      <GraphPlotItem
        key={key}
        plot={plot}
        fullPage={fullPage}
        showHideCallback={(show: boolean): void => showHidePlot(index, show)}
        editCallback={isBreakdown ? undefined : (): void => openEditPlotModal(index)}
        deleteCallback={isBreakdown ? undefined : (): void => {
          if (deletePlotCallback) {
            deletePlotCallback(index);
          }
        }}
        exploreLogsHref={plotData?.length ? createEventsPageUrl(startTime, endTime, config, plotData, index, false, true) : ''}
        explorePlotHref={plotData?.length ? createEventsPageUrl(startTime, endTime, config, plotData, index, true, true) : ''}
      />
    );
  });

  return (
    <div className="GraphPlotList">
      {plotList}

      {!isBreakdown && (
        <Button
          type="button"
          rightIcon={<TrendingUp />}
          className={`GraphPlotList__AddPlotButton ${plotList.length > 0 && 'GraphPlotList__AddPlotButton--TopBuffer'}`}
          onClick={openAddPlotModal}
          testId={ELEMENT.dashboardAddGraphAddPlot}
        >
          Add Plot
        </Button>
      )}
      {showEditPlot ? (
        <AddEditPlotsModal
          isEdit
          showModal={showEditPlot}
          plot={selectedPlot}
          plotIndex={selectedPlotIndex}
          plotModalSubmitCallback={editSelectedPlot}
          handleClose={(): void => {
            setShowEditPlot(false);
          }}
        />
      ) : null}
      {showAddPlot
        && (
          <AddEditPlotsModal
            showModal={showAddPlot}
            isEdit={false}
            plot={defaultPlot}
            plotIndex={plotsToDisplay.length}
            plotModalSubmitCallback={addPlotCallback}
            handleClose={(): void => {
              setShowAddPlot(false);
            }}
          />
        )}
      {!showAddPlot && <div className="GraphPlotList__BottomSpacer" />}
    </div>
  );
}

export default GraphPlotList;
